import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { SecondaryButton, TertiaryButton, TextField } from '../AtomComponents';
import { Checklist } from '../../_lib/icons';
import { Star } from '@material-ui/icons';
import useDataService from '../../hooks/useDataService';
import apiToUrlMap from '../../ApiMapping';
import { GridApi } from 'ag-grid-community';
import { onlyDecimalNumberKey, onlyNumberKey } from '../PreOrders/Utils';
import { InputAdornment } from '@material-ui/core';
import { dataFormatting } from '../../_lib/lib';
import { cloneDeep, mapValues } from 'lodash';

const initialThresholdValues = {
  minUnits: 0,
  minSubTotal: 0,
  minPercentFulfilled: 0,
};

interface IThresholdValues {
  minUnits: number;
  minSubTotal: number;
  minPercentFulfilled: number;
}

function ThresholdSection({
  setLoading,
  gridApi,
}: {
  setLoading: Dispatch<SetStateAction<boolean>>;
  gridApi: GridApi | undefined;
}) {
  const [thresholdValues, setThresholdValues] = useState<IThresholdValues>(initialThresholdValues);
  const initialThresholdValuesRef = useRef<IThresholdValues>();
  const { openSnackBar, fetchUrl } = useDataService();

  useEffect(() => {
    async function getAndSetThresholdLimits() {
      try {
        setLoading(true);
        const res = await fetchUrl('GET', apiToUrlMap.getCOThreshold);
        //format initial value to string
        const formatedRes: any = {
          minUnits: `${res.minUnits}` || '0',
          minPercentFulfilled: res.minPercentFulfilled?.toFixed(2) || '0.00',
          minSubTotal: res.minSubTotal?.toFixed(2) || '0.00',
        };
        setThresholdValues(formatedRes);
        initialThresholdValuesRef.current = formatedRes;
        setLoading(false);
      } catch (error: any) {
        openSnackBar('Error fetching threshold: ' + error.message, 'error');
      }
    }
    getAndSetThresholdLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectOrdersBasedOnThreshold() {
    if (!gridApi) return;
    const allRowDetails = gridApi.getRenderedNodes();
    allRowDetails.forEach((rowNode) => {
      const { data } = rowNode;
      if (
        data.orderQuantity >= +thresholdValues.minUnits ||
        data.orderPrice >= +thresholdValues.minSubTotal ||
        (data.totalNewFulfilled / data.totalAwarded) * 100 >= +thresholdValues.minPercentFulfilled
      ) {
        rowNode.setSelected(true);
        return;
      }
      rowNode.setSelected(false);
    });
  }

  function onChangeOfThreshold(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    const decimalValue = value.split('.')[1];
    switch (name) {
      case 'minSubTotal':
        if (+value > 999999999.99 || decimalValue?.length > 2) return;
        break;
      case 'minPercentFulfilled':
        if (+value > 100 || decimalValue?.length > 2) return;
        break;
    }
    setThresholdValues({ ...thresholdValues, [name]: value });
  }

  function onBlur(e: any) {
    const { name, value } = e.target;
    //threshold would be as string
    const updatedVal = ['minSubTotal', 'minPercentFulfilled'].includes(name)
      ? (+value).toFixed(2)
      : value || 0;
    setThresholdValues({ ...thresholdValues, [name]: updatedVal });
  }

  async function saveThresholdValues() {
    try {
      setLoading(true);
      //always convert to Number and save the threshold
      const updatedValues = mapValues(thresholdValues, (value) => Number(value));
      await fetchUrl('POST', apiToUrlMap.getCOThreshold, { body: updatedValues });
      initialThresholdValuesRef.current = cloneDeep(thresholdValues);
      openSnackBar('Threshold Defaults Set', 'success');
    } catch (error: any) {
      openSnackBar('error while saving threshold values' + error.message, 'error');
    }
    setLoading(false);
  }

  return (
    <div className="cell small-12 margin-bottom-2 px-one-line-data">
      <SecondaryButton startIcon={<Checklist />} onClick={selectOrdersBasedOnThreshold}>
        SELECT BY THRESHOLD
      </SecondaryButton>
      <TextField
        name="minUnits"
        label="Minimum Order Quantity"
        className="margin-left-2"
        type="number"
        inputProps={{ min: 0, step: 1 }}
        value={thresholdValues.minUnits}
        onBlur={onBlur}
        onChange={onChangeOfThreshold}
        onKeyPress={onlyNumberKey}
      />
      <TextField
        name="minSubTotal"
        label="Minimum Order Price"
        className="margin-left-2"
        type="number"
        inputProps={{ min: 0, step: 0.01 }}
        value={thresholdValues.minSubTotal}
        onChange={onChangeOfThreshold}
        onBlur={onBlur}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {dataFormatting('currency', ' ').trim()}
            </InputAdornment>
          ),
        }}
        onKeyPress={onlyDecimalNumberKey}
      />
      <TextField
        name="minPercentFulfilled"
        label="Minimum % of PO"
        type="number"
        size="medium"
        inputProps={{ min: 0, step: 0.01 }}
        onBlur={onBlur}
        className="margin-left-2"
        value={thresholdValues.minPercentFulfilled}
        onChange={onChangeOfThreshold}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
        onKeyPress={onlyDecimalNumberKey}
      />
      <TertiaryButton
        startIcon={<Star />}
        className="margin-left-2"
        onClick={saveThresholdValues}
        disabled={
          JSON.stringify(thresholdValues) === JSON.stringify(initialThresholdValuesRef.current)
        }>
        SET AS DEFAULT
      </TertiaryButton>
    </div>
  );
}

export default ThresholdSection;
